import {
  Box,
  Button,
  ButtonProps,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useColorModeValue,
  useFormControl,
} from "@chakra-ui/react";
import { RiArrowDownSLine } from "@remixicon/react";
import { forwardRef } from "react";

import {
  AttributeFilterOperator,
  operatorDisplayName,
  OperatorsByType,
} from "@reflag/shared/attributeFilter";

type RuleOperatorProps = Omit<ButtonProps, "value" | "onChange"> & {
  value: AttributeFilterOperator | undefined;
  onChange: (nextValue: AttributeFilterOperator) => void;
  operators: Partial<typeof OperatorsByType>;
};

const arrayDescriptions: Partial<Record<AttributeFilterOperator, string>> = {
  ANY_OF:
    'The array contains at least one selected value. Selecting "admin" and "editor" matches ["editor", "viewer"]. Case-sensitive.',
  NOT_ANY_OF:
    'The array contains none of the selected values. Selecting "admin" and "editor" matches ["viewer"], but not ["editor", "viewer"]. Case-sensitive.',
  CONTAINS:
    'The array contains the selected value as a whole item. Selecting "admin" matches ["admin", "viewer"], but not ["administrator"]. Case-sensitive.',
  NOT_CONTAINS:
    'The array does not contain the selected value as a whole item. Selecting "admin" matches ["editor", "viewer"], but not ["admin", "viewer"]. Case-sensitive.',
  IS: 'The array must have exactly one item equal to the selected value. Selecting "admin" matches ["admin"], but not ["admin", "viewer"] or ["admin", "admin"]. Case-sensitive.',
  IS_NOT:
    'Excludes an array with exactly one item equal to the selected value. For "admin", [], ["viewer"], and ["admin", "viewer"] match; ["admin"] does not. Case-sensitive.',
  SET: 'The array has at least one item. For example, ["admin"] matches, but [] does not.',
  NOT_SET: "The array is empty ([]) or the attribute is missing.",
};

const operatorDescriptions: Partial<Record<AttributeFilterOperator, string>> = {
  IS: 'For text, the entire value must equal the selected value. Selecting "admin" excludes "Admin" and "administrator". Case-sensitive.',
  IS_NOT:
    'For text, the value must differ from the selected value. Selecting "admin" includes "viewer" and "Admin", but excludes "admin". Case-sensitive.',
  ANY_OF:
    'For text, the value must equal one of the selected values. Selecting "admin" and "editor" matches "editor", but not "viewer". Case-sensitive.',
  NOT_ANY_OF:
    'For text, the value must differ from every selected value. Selecting "admin" and "editor" matches "viewer", but neither "admin" nor "editor". Case-sensitive.',
  CONTAINS:
    'Finds text anywhere in the value. Searching for "admin" matches "Administrator". Case-insensitive.',
  NOT_CONTAINS:
    'The value must not contain the text anywhere. Searching for "admin" matches "viewer", but not "Administrator". Case-insensitive.',
  SET: "Checks whether a value is present, not whether it is true. For example, 0 and false count as values.",
  NOT_SET:
    "Checks for a missing value, not a false value. For example, 0 and false do not match; an empty array ([]) does.",
  AFTER:
    "The date is later than the cutoff, counted back from now. With 7 days, dates 2 days ago or in the future match.",
  BEFORE:
    "The date is earlier than the cutoff, counted back from now. With 7 days, a date 10 days ago matches; a date 2 days ago does not.",
};

const RuleOperator = forwardRef<HTMLButtonElement, RuleOperatorProps>(
  ({ value, onChange, operators, ...rest }, ref) => {
    const formControlProps = useFormControl<HTMLButtonElement>(rest);
    const activeColor = useColorModeValue("brand.500", "brand.300");

    // "List" describes the rule input, not the attribute. Keep these choices
    // beside scalar equality rather than presenting a competing List group.
    const groups = {
      ...operators,
      any: [...(operators.any ?? []), ...(operators.list ?? [])],
      list: [],
    };
    const hasArrayGroup = !!operators.array?.length;
    const hasMultipleGroups =
      Object.entries(groups).filter(([_, ops]) => ops.length > 0).length > 1;

    return (
      <Menu size="xs">
        <MenuButton
          ref={ref}
          as={Button}
          background="appBackground"
          data-testid="rule-operator"
          isDisabled={formControlProps.disabled}
          rightIcon={
            <Box fontSize="xl" mr={-2}>
              <RiArrowDownSLine size={16} />
            </Box>
          }
          variant="input"
          {...formControlProps}
        >
          {value && (
            <Text display="block" noOfLines={1}>
              {operatorDisplayName[value]}
            </Text>
          )}
        </MenuButton>
        <MenuList data-testid="rule-operator-list" maxH="sm" overflowY="auto">
          {Object.entries(groups).map(([type, ops], index) => {
            if (ops.length === 0) return null;
            return (
              <MenuGroup
                key={index}
                borderTop="1px solid"
                borderTopColor="appBorder"
                color="dimmed"
                fontSize="xs"
                m={0}
                pb={1}
                pt={2}
                px={3}
                textTransform="capitalize"
                title={
                  type === "array"
                    ? "Array"
                    : !hasMultipleGroups
                      ? undefined
                      : hasArrayGroup
                        ? "Current operator"
                        : type === "any"
                          ? "General"
                          : type
                }
              >
                {ops.map((operator, index) => (
                  <Tooltip
                    key={index}
                    label={
                      type === "array"
                        ? arrayDescriptions[operator]
                        : operatorDescriptions[operator]
                    }
                    placement="right"
                    openDelay={200}
                    hasArrow
                  >
                    <MenuItem
                      color={value === operator ? activeColor : undefined}
                      data-testid={`operator-${operator}`}
                      pl={6}
                      onClick={() => onChange(operator)}
                    >
                      {operatorDisplayName[operator]}
                    </MenuItem>
                  </Tooltip>
                ))}
              </MenuGroup>
            );
          })}
        </MenuList>
      </Menu>
    );
  },
);

RuleOperator.displayName = "RuleOperator";

export default RuleOperator;
